import moment from "moment";
import "moment/locale/es";
moment.locale('es');

// Mismo lenguaje visual que el PDF de la pre cotizacion: la paleta del formato de papel del
// cliente como acentos sobre paneles claros, sin rejillas.
const COLOR = {
  azul: '#288AC1',
  azulOscuro: '#1D5F86',
  azulTinte: '#EAF4FA',
  oro: '#E8B84B',
  durazno: '#E8935F',
  rojo: '#C0392B',
  verde: '#4E9A4E',
  gris: '#9AA6B2',
  amarilloTinte: '#FDF3D3',
  texto: '#1F2933',
  textoSuave: '#728090',
  panel: '#F5F8FB',
  divisor: '#E4EAF0',
  casillaVacia: '#A9B4BF',
  blanco: '#FFFFFF',
};

const TOLERANCIA_ESTANDAR = 'TOLERANCIA ESTANDAR +-.5mm';

const ETIQUETA_AVANCE = { 1: 'PENDIENTE', 2: 'EN PROCESO', 3: 'TERMINADO' };
const COLOR_AVANCE = { 1: COLOR.gris, 2: COLOR.durazno, 3: COLOR.verde };

const ESTADO_ORDEN = {
  0: ['CANCELADA', COLOR.rojo],
  1: ['ABIERTA', COLOR.azul],
  2: ['EN PROCESO', COLOR.durazno],
  3: ['TERMINADA', COLOR.verde],
};

const MARGEN = 34;
const ANCHO_CONTENIDO = 612 - MARGEN * 2; // LETTER

// ---------------------------------------------------------------------------------------
// Lenguaje visual (el mismo en preCotizacionIngenieriaPdf.js). Cada tabla arma su propio
// arreglo de widths: pdfmake los muta.
// ---------------------------------------------------------------------------------------

const sinBordes = {
  hLineWidth: () => 0,
  vLineWidth: () => 0,
  paddingLeft: () => 0,
  paddingRight: () => 0,
  paddingTop: () => 0,
  paddingBottom: () => 0,
};

const panel = (acento = COLOR.azul) => ({
  hLineWidth: (i) => (i === 0 ? 1.5 : 0),
  vLineWidth: () => 0,
  hLineColor: () => acento,
  fillColor: () => COLOR.panel,
  paddingLeft: () => 10,
  paddingRight: () => 10,
  paddingTop: (i) => (i === 0 ? 9 : 5),
  paddingBottom: (i, node) => (i === node.table.body.length - 1 ? 9 : 5),
});

const mostrar = (valor) => (valor === 0 ? '0' : valor === undefined || valor === null || valor === '' ? ' ' : String(valor));

const fecha = (val) => (val ? moment(val).format('DD/MM/YYYY') : '');

const titulo = (texto, color = COLOR.azulOscuro) => ({
  text: texto.toUpperCase(),
  fontSize: 8.5,
  bold: true,
  color,
  margin: [0, 16, 0, 5],
  headlineLevel: 1,
});

const evitarTituloHuerfano = (nodo, siguientesEnPagina) => nodo.headlineLevel === 1 && siguientesEnPagina.length === 0;

const campo = (etiqueta, valor, extra = {}) => ({
  stack: [
    { text: etiqueta.toUpperCase(), style: 'etiqueta' },
    { text: mostrar(valor), style: 'valor', margin: [0, 2, 0, 0] },
  ],
  ...extra,
});

const campoNodo = (etiqueta, nodo, extra = {}) => ({
  stack: [{ text: etiqueta.toUpperCase(), style: 'etiqueta' }, { ...nodo, margin: [0, 3, 0, 0] }],
  ...extra,
});

const casilla = (texto, activo) => ({
  width: 'auto',
  columns: [
    {
      width: 11,
      canvas: [
        {
          type: 'rect',
          x: 0,
          y: 1.5,
          w: 8.5,
          h: 8.5,
          lineWidth: 0.8,
          lineColor: activo ? COLOR.azul : COLOR.casillaVacia,
          color: activo ? COLOR.azul : COLOR.blanco,
        },
        ...(activo
          ? [
              {
                type: 'polyline',
                lineWidth: 1.3,
                lineColor: COLOR.blanco,
                points: [{ x: 2, y: 5.9 }, { x: 3.8, y: 7.8 }, { x: 6.8, y: 3.8 }],
              },
            ]
          : []),
      ],
    },
    { width: 'auto', text: texto, style: 'valor', noWrap: true, margin: [2, 0, 0, 0] },
  ],
  columnGap: 0,
});

const casillas = (opciones) => ({
  columns: opciones.map(([texto, activo]) => casilla(texto, activo)),
  columnGap: 16,
});

const pastilla = (texto, color, fontSize = 7) => ({
  table: {
    widths: ['auto'],
    body: [[{ text: texto, bold: true, fontSize, color: COLOR.blanco, fillColor: color, alignment: 'center', margin: [9, 2.5, 9, 2.5] }]],
  },
  layout: sinBordes,
});

const alDerecha = (nodo, margin = [0, 0, 0, 0]) => ({ columns: [{ width: '*', text: '' }, { width: 'auto', ...nodo }], margin });

const encabezado = ({ logo, nombreEmpresa, titulo: tituloDocumento, subtitulo, folio, insignias = [], detalles = [] }) => [
  {
    columns: [
      logo
        ? { width: 160, image: 'logoEmpresa', fit: [150, 60] }
        : { width: 160, text: nombreEmpresa, bold: true, fontSize: 12, color: COLOR.azulOscuro, margin: [0, 14, 0, 0] },
      {
        width: '*',
        stack: [
          { text: tituloDocumento, bold: true, fontSize: 18, color: COLOR.azulOscuro, alignment: 'right' },
          { text: subtitulo, fontSize: 8, color: COLOR.textoSuave, alignment: 'right', margin: [0, 1, 0, 0] },
          alDerecha(
            {
              columns: [
                ...insignias.map(([texto, color]) => ({ width: 'auto', ...pastilla(texto, color), margin: [0, 2, 0, 0] })),
                // El folio en azul oscuro: los colores de estado (durazno, verde, rojo) quedan
                // para las pastillas de estado y no se confunden con el.
                ...(folio ? [{ width: 'auto', ...pastilla(folio, COLOR.azulOscuro, 11) }] : []),
              ],
              columnGap: 6,
            },
            [0, 6, 0, 0]
          ),
          ...detalles.map((linea) => ({ text: linea, fontSize: 7.5, color: COLOR.textoSuave, alignment: 'right', margin: [0, 4, 0, 0] })),
        ],
      },
    ],
  },
  {
    canvas: [
      { type: 'rect', x: 0, y: 0, w: ANCHO_CONTENIDO, h: 2, color: COLOR.azul },
      { type: 'rect', x: 0, y: 0, w: 96, h: 2, color: COLOR.oro },
    ],
    margin: [0, 10, 0, 0],
  },
];

const pie = (izquierda) => (paginaActual, totalPaginas) => ({
  margin: [MARGEN, 12, MARGEN, 0],
  columns: [
    { text: izquierda, fontSize: 6.5, color: COLOR.textoSuave },
    { text: `${paginaActual} / ${totalPaginas}`, fontSize: 6.5, color: COLOR.textoSuave, alignment: 'right' },
  ],
});

// ---------------------------------------------------------------------------------------
// Tarjeta MATERIAL #n
// ---------------------------------------------------------------------------------------

// Panel con una franja a la izquierda del color de su avance. Una sola tabla y
// `unbreakable`: un material nunca se parte entre paginas.
const tarjeta = {
  hLineWidth: (i, node) => (i === 0 || i === 1 || i === node.table.body.length ? 0 : 0.5),
  vLineWidth: () => 0,
  hLineColor: () => COLOR.divisor,
  fillColor: () => COLOR.panel,
  paddingLeft: (columna) => (columna === 0 ? 0 : 10),
  paddingRight: (columna) => (columna === 0 ? 0 : 10),
  paddingTop: (fila) => (fila === 0 ? 8 : 5),
  paddingBottom: (fila, node) => (fila === node.table.body.length - 1 ? 8 : 5),
};

const bloqueMaterial = (material, indice, nombreEmpresa) => {
  const avance = material?.avance || 1;
  const toleranciaEspecial = (material?.toleranciaEspecial || '').trim();
  const nombreMaterial = (material?.material || '').trim();
  const FILAS = 6;

  return {
    table: {
      widths: [4, '*', '*', '*', '*'],
      body: [
        [
          { text: '', fillColor: COLOR_AVANCE[avance], rowSpan: FILAS },
          {
            text: [
              { text: `MATERIAL #${indice + 1}`, color: COLOR.azul },
              ...(nombreMaterial ? [{ text: `   ${nombreMaterial}`, color: COLOR.texto }] : []),
            ],
            bold: true,
            fontSize: 10,
            colSpan: 3,
          },
          {},
          {},
          alDerecha(pastilla(ETIQUETA_AVANCE[avance], COLOR_AVANCE[avance])),
        ],
        [{}, campo('Procesos', material?.procesos, { colSpan: 4 }), {}, {}, {}],
        [
          {},
          campo('Material', material?.material),
          campo('Calibre', material?.calibre),
          campo('Dimensiones', material?.dimensiones),
          campo('Cantidad de material', material?.cantidad),
        ],
        [
          {},
          campoNodo(
            'Suministra',
            casillas([
              [nombreEmpresa, !!material?.suministroEmpresa],
              ['Cliente', !!material?.suministroCliente],
            ]),
            { colSpan: 2 }
          ),
          {},
          campoNodo(
            'Estatus del material',
            casillas([
              ['En piso', !!material?.enPiso],
              ['Compra', !!material?.enCompra],
              ['En espera del cliente', !!material?.enEsperaCliente],
            ]),
            { colSpan: 2 }
          ),
          {},
        ],
        [
          {},
          campo('Cantidad de piezas', material?.cantidadPiezas ?? ''),
          campo(toleranciaEspecial ? 'Tolerancia especial' : 'Tolerancia', toleranciaEspecial || TOLERANCIA_ESTANDAR, {
            colSpan: 3,
            fillColor: COLOR.amarilloTinte,
          }),
          {},
          {},
        ],
        [{}, campo('Notas', material?.notas, { colSpan: 4 }), {}, {}, {}],
      ],
    },
    layout: tarjeta,
    margin: [0, 0, 0, 10],
    unbreakable: true,
  };
};

// ---------------------------------------------------------------------------------------
// Documento
// ---------------------------------------------------------------------------------------

const ordenDeMaquilaPdf = (values) => {
  const ordenDeMaquila = values?.ordenDeMaquila || {};
  const empresa = values?.empresa || {};

  const materiales = ordenDeMaquila.materiales || [];
  const tieneLogo = !!empresa?.logo?.base64Completa;
  const nombreEmpresaCompleto = empresa?.nombreComercial || empresa?.nombre || '';
  const nombreEmpresa = (nombreEmpresaCompleto || 'EMPRESA').toUpperCase();
  const estado = ESTADO_ORDEN[ordenDeMaquila.estado];

  const dd = {
    pageSize: 'LETTER',
    pageMargins: [MARGEN, MARGEN, MARGEN, 40],
    pageBreakBefore: evitarTituloHuerfano,
    content: [
      ...encabezado({
        logo: tieneLogo,
        nombreEmpresa: nombreEmpresaCompleto,
        titulo: 'Orden de maquila',
        subtitulo: 'Producción',
        folio: ordenDeMaquila.folio,
        insignias: estado ? [estado] : [],
        detalles: [ordenDeMaquila.fechaCompromiso ? `Compromiso ${fecha(ordenDeMaquila.fechaCompromiso)}` : null].filter(Boolean),
      }),

      titulo('Datos generales'),
      {
        table: {
          widths: ['25%', '25%', '25%', '25%'],
          body: [
            [
              campo('Proyecto', ordenDeMaquila.nombreProyecto, { colSpan: 2 }),
              {},
              campo('Encargado', ordenDeMaquila.encargadoProyecto),
              campo('Núm. de cotización', ordenDeMaquila.numeroCotizacion),
            ],
            [
              campo('Cliente', ordenDeMaquila.cliente),
              campo('Contacto', ordenDeMaquila.contacto),
              campo('Fecha de solicitud', fecha(ordenDeMaquila.fechaSolicitud)),
              campo('Fecha compromiso', fecha(ordenDeMaquila.fechaCompromiso)),
            ],
          ],
        },
        layout: panel(),
      },

      titulo('Programación de entrega'),
      {
        table: {
          widths: ['50%', '25%', '25%'],
          body: [[
            campoNodo(
              'Tipo',
              casillas([
                ['Recolección (cliente)', ordenDeMaquila.recoleccionOEntrega === 'recoleccion'],
                ['Entrega (chofer)', ordenDeMaquila.recoleccionOEntrega === 'entrega'],
              ])
            ),
            campo('Fecha de recolección', fecha(ordenDeMaquila.fechaRecoleccion)),
            campo('Fecha de entrega', fecha(ordenDeMaquila.fechaEntrega)),
          ]],
        },
        layout: panel(),
      },

      titulo(`Materiales${materiales.length ? ` (${materiales.length})` : ''}`),
      ...(materiales.length > 0
        ? materiales.map((material, indice) => bloqueMaterial(material, indice, nombreEmpresa))
        : [
            {
              table: {
                widths: ['*'],
                body: [[{ text: 'Sin materiales capturados', color: COLOR.textoSuave, italics: true, alignment: 'center' }]],
              },
              layout: panel(),
            },
          ]),

      titulo('Notas'),
      {
        table: {
          widths: ['*'],
          body: [[{ text: mostrar(ordenDeMaquila.notas), style: 'valor', margin: [0, 0, 0, 18] }]],
        },
        layout: panel(),
      },
    ],
    footer: pie([nombreEmpresaCompleto, ordenDeMaquila.folio ? `Orden de maquila ${ordenDeMaquila.folio}` : 'Orden de maquila'].filter(Boolean).join('  ·  ')),
    images: tieneLogo ? { logoEmpresa: empresa.logo.base64Completa } : {},
    defaultStyle: { fontSize: 7.5, color: COLOR.texto },
    styles: {
      etiqueta: { fontSize: 6.3, bold: true, color: COLOR.textoSuave },
      valor: { fontSize: 9, color: COLOR.texto },
    },
  };

  return dd;
};

export default ordenDeMaquilaPdf;
